import {formatReferenceCopy} from 'utils/typography';
import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from "@mui/material/Button";
import Pagination from '@mui/material/Pagination';
import Divider from "@mui/material/Divider";
import { Close as CloseIcon } from '@vedmarket/ui-kit';
import { Check as CheckIcon } from '@vedmarket/ui-kit';
import CircularProgress from "@mui/material/CircularProgress";
import {Filters, Preloader} from "@vedmarket/ui-kit";

import {
  useExtraServicesId,
  useGetExtraServicesCompaniesForFilterQuery,
  useGetExtraServicesSearchByOrderIdMutation,
  useParamsExtraServicesData,
} from "../../../../../../../api/calc";
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";
import {useDemoAdditionalModal} from "components/cabinet/demo/steps/additional-services/use-additional-services";

import CompanyCard from "../company-card";
import {numberWithSpaces} from "../../../../../../../utils/helpers";
import {ADORNMENTS, COLORS} from "../../../../../../../config/constants";

import './modalSearch.scss';

export const enum SORTING_OPTIONS {
    PRICE_DESK = 'price',
    PRICE_ASC = '-price',
}

export const enum RESULTS_COUNT {
    COUNT_10 = 10,
    COUNT_30 = 30,
    COUNT_45 = 45
}

type Props = {
    preloadCompanies?: {
      results: ExtraService[]
      count: number
      meta: {
        max_price: number
        min_price: number
      }
    }
    refetchGetExtraServices(): void
    handleClose(): void
    handleSave(): void
    setTotalServicesCost(totalServicesCost: number): void
    setInitialValuesExtraServices(values: number[]): void
}

type FilterValues = {
  cost: [number, number]
  companyIds: number[]
}

const ModalSearch = ({
  preloadCompanies,
  handleClose,
  setTotalServicesCost,
  refetchGetExtraServices,
  handleSave,
  setInitialValuesExtraServices,
} : Props ) => {

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute()

  const [ companies, setCompanies ] = React.useState<ExtraService[]>(preloadCompanies?.results || []);
  const [ totalCompanies, setTotalCompanies ] = React.useState(preloadCompanies?.results.length);
  const [ minTotal, setMinTotal ] = React.useState(preloadCompanies?.meta?.min_price || 0);
  const [ maxTotal, setMaxTotal ] = React.useState(preloadCompanies?.meta?.max_price || 10000);
  const [ filtersValues, setFiltersValues ] = React.useState<FilterValues>({
    cost: [ preloadCompanies?.meta?.min_price || 0, preloadCompanies?.meta?.max_price || 10000 ],
    companyIds: [],
  });

  const [ showProgressBar, setShowProgressBar ] = useState(true);
  const [ filtersAreApplying, setFiltersAreApplying ] = useState(false);

  const [ getCompanies ] = useGetExtraServicesSearchByOrderIdMutation()
  const { data: companiesList } = useGetExtraServicesCompaniesForFilterQuery(orderId)

  const filterData = useParamsExtraServicesData();

  useEffect(() => {
    if (preloadCompanies?.results){
      setCompanies(preloadCompanies?.results);
      setTotalCompanies(preloadCompanies?.count || preloadCompanies?.results.length);
    }
    if (preloadCompanies){
      const nextMinTotal = preloadCompanies.meta?.min_price || 0;
      const nextMaxTotal = preloadCompanies.meta?.max_price || 10000;

      setMinTotal(nextMinTotal);
      setMaxTotal(nextMaxTotal)
      setFiltersValues((state) => ({
        ...state,
        cost: [ nextMinTotal, nextMaxTotal ],
      }));
    }
  }, [ preloadCompanies ])

  useEffect(() => {
    if (companiesList) {
      const companyIds = companiesList.map((item) => item.id);

      setFiltersValues((state) => ({
        ...state,
        companyIds,
      }));
    }
  }, [ companiesList ])

  useEffect(() => {
    setTimeout(() => {
      setShowProgressBar(false)
    }, 1000);
  }, [ ])

  const companiesRequest = (data?: OrdersParamsExtraServices) => {
    return getCompanies({
      orderId,
      ...filterData,
      ...data,
    }).then((data) => {
      // @ts-ignore
      setCompanies(data.data.results);
      // @ts-ignore
      setTotalCompanies(data.data.count);
    });
  }

  const handleChangeResultsCount = (value: string | RESULTS_COUNT ) => companiesRequest({
    limit: Number(value)
  });

  const handleChangeCurrentPage = (event: React.ChangeEvent<unknown>, pageNumber: number) => companiesRequest({
    page: pageNumber
  });

  const resultsCount = filterData.limit || RESULTS_COUNT.COUNT_30;
  const companyIds = companiesList?.map((item) => item.id) || [];
  const companyOptions = companiesList?.map((item) => ({
    value: item.id,
    label: item.name,
  })) || [];

  const userServices = useExtraServicesId();
  const demoPlayback = useDemoAdditionalModal(Boolean(userServices?.length))

  const totalServicesCost = useMemo(() => {

    if (!userServices?.length || !companies?.length) return 0;

    const selectedIds = new Set(userServices);

    return companies.reduce((total, company) => {
      const companySum = company.services
        .filter(service => selectedIds.has(service.id))
        .reduce((sum, service) => sum + service.price, 0);

      return total + companySum;
    }, 0);

  }, [ userServices, companies ]);

  useEffect(() => {
    setTotalServicesCost(totalServicesCost);
  }, [ totalServicesCost ]);

  const handleChangeFilters = (values: FilterValues) => {
    setFiltersValues(values);
  }

  const handleApplyFilters = (values: FilterValues) => {
    const isDefaultCost = values.cost[0] === minTotal && values.cost[1] === maxTotal;
    const hasDefaultCompanyIds = values.companyIds.length === companyIds.length
      && companyIds.every((item) => values.companyIds.includes(item));

    setFiltersAreApplying(true);

    companiesRequest({
      price_min: isDefaultCost ? undefined : values.cost[0],
      price_max: isDefaultCost ? undefined : values.cost[1],
      company_id: hasDefaultCompanyIds ? undefined : values.companyIds,
    }).finally(() => {
      setFiltersAreApplying(false);
    });
  }

  const renderCompanies = () => {
    if (filtersAreApplying) {
      return <Preloader />
    }

    return (<>
      {!totalCompanies && <div className='search-results__results'>
        <Typography variant='h6' gutterBottom sx={{marginTop: 1}}>
          {t('actions__message__searchNotFound')}
        </Typography>
        <Divider sx={{marginBottom: 3}}/>
      </div>}
      {companies.map((company) => <CompanyCard
        key={company.id}
        company={company}
        refetchGetExtraServices={refetchGetExtraServices}
        setInitialValuesExtraServices={setInitialValuesExtraServices}
      />
      )}
      {Number(totalCompanies) > resultsCount && <Pagination
        count={Math.ceil(Number(totalCompanies) / resultsCount)}
        sx={{marginTop: 3}}
        size='large'
        onChange={handleChangeCurrentPage}
      />}
    </>)
  }

  return (
    <div className='modal-search-container'>
      <div className='search-results'>
        {showProgressBar ? <CircularProgress/> : (
          <div className='search-results__container'>
            <div className='search-results__results'>
              <div className='search-results__sorting'>
                <FormControl sx={{width: '324px'}}>
                  <InputLabel shrink id='sorting-select'>{t('common__text__sort')}</InputLabel>
                  <Select
                    notched
                    labelId='sorting-select'
                    id='sorting-select'
                    value={filterData.order_by || SORTING_OPTIONS.PRICE_DESK}
                    label={t('common__text__sort')}
                    onChange={({target: {value}}) => companiesRequest({
                      order_by: value
                    })}
                    className='search-results__select-sorting'>
                    <MenuItem
                      value={SORTING_OPTIONS.PRICE_DESK}>{t('dictionaries__text__certification__priceAscendingOrder')}</MenuItem>
                    <MenuItem
                      value={SORTING_OPTIONS.PRICE_ASC}>{t('dictionaries__text__certification__priceDescendingOrder')}</MenuItem>
                  </Select>
                </FormControl>
                <FormControl>
                  <InputLabel shrink id='count-select'>{t('common__text__resultsCount')}</InputLabel>
                  <Select
                    notched
                    labelId='count-select'
                    id='count-select'
                    value={resultsCount}
                    label={t('common__text__resultsCount')}
                    onChange={({target: {value}}) => handleChangeResultsCount(value)}
                    className='search-results__select-count'>
                    <MenuItem value={RESULTS_COUNT.COUNT_10}>10</MenuItem>
                    <MenuItem value={RESULTS_COUNT.COUNT_30}>30</MenuItem>
                    <MenuItem value={RESULTS_COUNT.COUNT_45}>45</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <Divider sx={{marginBottom: 3}}/>
              {renderCompanies()}
              <div className='additional-search-actions'>
                <Button
                  variant='outlined'
                  startIcon={<CloseIcon style={{ width: '22px', height: '22px' }} />}
                  className='modal-search-container__cancel'
                  sx={{
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={handleClose}
                >{t('actions__text__logOutWithoutSaving')}</Button>
                <Button
                  {...demoPlayback.buttonProps}
                  variant='contained'
                  startIcon={<CheckIcon style={{ width: '22px', height: '22px' }} />}
                  className='modal-search-container__save'
                  sx={{
                    flexGrow: 1,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={handleSave}
                >{totalServicesCost ? `${t('common__text__inTotal')}: ${numberWithSpaces(totalServicesCost)} USD. ${t('actions__text__saveAndExit')}` : t('actions__text__saveAndExit')}</Button>
              </div>
            </div>
            <Card className='search-results__filters'>
              <CardContent sx={{
                padding: 0,
                '&:last-child': {
                  paddingBottom: 0
                }
              }}>
                <Typography variant='h6' sx={{
                  padding: '16px 24px',
                  color: COLORS.BLACK_DARK,
                  fontSize: '18px',
                  fontWeight: 500,
                  lineHeight: '32px',
                  letterSpacing: 0,
                }}>
                  {formatReferenceCopy(t('common__text__filters'))}
                </Typography>
                <Filters
                  applyButtonText={t('actions__text__applyFilters')}
                  onApply={(values) => handleApplyFilters(values as FilterValues)}
                  onChange={(values) => handleChangeFilters(values as FilterValues)}
                  sections={[ {
                    type: 'range',
                    key: 'cost',
                    title: t('dictionaries__text__certification__costOfServices'),
                    defaultValues: [ minTotal, maxTotal ],
                    min: minTotal,
                    max: maxTotal,
                    selectedValues: filtersValues.cost,
                    fromLabel: t('dictionaries__text__additional__costOfServicesFrom'),
                    toLabel: t('dictionaries__text__additional__costOfServicesTo'),
                    valuePrefix: 'USD',
                    inputAdornment: ADORNMENTS.PRICE,
                  }, {
                    key: 'companyIds',
                    title: t('dictionaries__text__additional__serviceProviders'),
                    defaultValues: companyIds,
                    selectedValues: filtersValues.companyIds,
                    options: companyOptions,
                  } ]}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}

export default ModalSearch
